import { usePersonReadDataContext } from '@aibees/elephant/context';
import {
  useWorkHistoryCurrentRoles,
  useWorkHistoryCurrentRolesInCompany,
  useWorkHistoryDuration,
  useWorkHistoryPresentRoles,
} from '@aibees/elephant/feature/search-table-people/components/people-drawer/components/contact-details/components/work-history';
import { getJobDuration } from '@aibees/elephant/feature/search-table-people/components/people-drawer/components/contact-details/components/work-history/utils';
import { humanReadablePersonLocation } from '@aibees/elephant/feature/search-table-people/components/people-drawer/components/person-job-info/utils';
import {
  useCompanyVisibilityState,
  useGetAbbreviationsLanguages,
} from '@aibees/elephant/hooks';
import { usePersonCompanyInfo } from '@aibees/elephant/hooks/people/use-person-company-info';
import {
  findTimeZoneByLocationCountry,
  humanReadableJobFunction,
  humanReadableSeniority,
} from '@aibees/elephant/utils';
import { TextEllipsis } from '@aibees/shared/components';
import { capitalize, fHumanReadableNumber } from '@aibees/shared/utils';
import { Typography } from '@mui/material';
import { useMemo } from 'react';
import { ContactOverviewItemProps } from '../contact-overview-item';

export function useContactOverviewItems() {
  const { api } = usePersonReadDataContext();
  const currentRolesInCompany = useWorkHistoryCurrentRolesInCompany();
  const allCurrentRoles = useWorkHistoryCurrentRoles();
  const allPresentRoles = useWorkHistoryPresentRoles();
  const fullWorkExpDuration = useWorkHistoryDuration();
  const { wasPreviouslySelfEmployedOrFreelancer, previousEmploymentType } =
    useCompanyVisibilityState();
  const { companyName } = usePersonCompanyInfo(api?.data);
  const currentRoleDuration = getJobDuration(
    currentRolesInCompany?.[0]?.date?.start,
    currentRolesInCompany?.[0]?.date?.end
  );
  const timeZone = findTimeZoneByLocationCountry(api?.data?.location?.country);
  const latestEducationItem = api?.data?.educations?.[0];
  const personLanguages = api?.data?.languages?.profile_languages
    ?.map((x) => x.name)
    .join(', ');
  const roleName = api?.data?.profile?.title;

  const abbreviationsLanguages = useGetAbbreviationsLanguages(personLanguages);

  return useMemo(() => {
    const items: ContactOverviewItemProps[] = [
      {
        icon: 'portrait',
        label: 'Job Title',
        value: roleName,
        content: (
          <TextEllipsis
            variant="body4"
            fontWeight={700}
            color="grey.700"
            value={roleName}
          />
        ),
        condition:
          (allPresentRoles?.reduce(
            (total, role) => total + (role.profile_positions?.length || 0),
            0
          ) ?? 0) > 1,
        badgeLength:
          (allPresentRoles?.reduce(
            (total, role) => total + (role.profile_positions?.length || 0),
            0
          ) ?? 0) - 1,
        type: 'job',
        showPreviousCompanyBadge: true,
      },

      {
        icon: 'medal-star',
        label: 'Headline',
        value: api?.data?.profile?.headline,
        content: (
          <TextEllipsis
            variant="body4"
            fontWeight={700}
            color="grey.700"
            value={api?.data?.profile?.headline || ''}
          />
        ),
      },
      {
        icon: 'linkedin',
        label: 'Followers',
        value: api?.data?.statistics?.network?.followers_count,
        content: (
          <Typography variant="body4" fontWeight={700} color="grey.700">
            {fHumanReadableNumber(
              api?.data?.statistics?.network?.followers_count ?? 0
            )}
          </Typography>
        ),
      },
      {
        icon: 'clock-line',
        label: 'Employee Type',
        value: currentRolesInCompany,
        content: (
          <Typography variant="body4" fontWeight={700} color="grey.700">
            {currentRolesInCompany?.[0]?.profile_positions?.[0]
              ?.employment_type || 'N/A'}
          </Typography>
        ),
      },
      {
        icon: 'location',
        label: 'Contact Location',
        value: api?.data?.profile?.title,
        content: (
          <TextEllipsis
            variant="body4"
            fontWeight={700}
            color="grey.700"
            value={humanReadablePersonLocation(api?.data?.location ?? null)}
          />
        ),
      },
      {
        icon: 'business',
        label: 'Company',
        value:
          wasPreviouslySelfEmployedOrFreelancer && previousEmploymentType
            ? previousEmploymentType
            : companyName,
        content: (
          <TextEllipsis
            variant="body4"
            fontWeight={700}
            color="grey.700"
            value={
              wasPreviouslySelfEmployedOrFreelancer && previousEmploymentType
                ? previousEmploymentType
                : companyName || ''
            }
          />
        ),
        condition: allPresentRoles && allPresentRoles.length > 1,
        badgeLength: allPresentRoles && allPresentRoles?.length - 1,
        type: 'company',
        showPreviousCompanyBadge: true,
      },
    ];

    const moreItems: ContactOverviewItemProps[] = [
      {
        icon: 'apartment-rounded',
        label: 'Department',
        value: api?.data?.department?.departments,
        content: (
          <Typography variant="body4" fontWeight={700} color="grey.700">
            {api?.data?.department?.departments &&
              capitalize(api?.data?.department?.departments[0])}
          </Typography>
        ),
      },
      {
        icon: 'cable-rounded',
        label: 'Seniority',
        value: api?.data?.department?.seniority,
        content: (
          <Typography variant="body4" fontWeight={700} color="grey.700">
            {humanReadableSeniority(api?.data?.department?.seniority)}
          </Typography>
        ),
      },
      {
        icon: 'calendar-view-month-outline',
        label: 'Time in Role',
        value: currentRoleDuration,
        content: (
          <TextEllipsis
            value={currentRoleDuration?.fullText}
            variant="body4"
            fontWeight={700}
            color="grey.700"
          />
        ),
      },
      {
        icon: 'work-outline',
        label: 'Work Experience',
        value: fullWorkExpDuration,
        content: (
          <TextEllipsis
            value={fullWorkExpDuration?.fullText}
            variant="body4"
            fontWeight={700}
            color="grey.700"
          />
        ),
      },
      {
        icon: 'function',
        label: 'Function',
        value: api?.data?.department?.functions,
        content: (
          <Typography variant="body4" fontWeight={700} color="grey.700">
            {humanReadableJobFunction(api?.data?.department?.functions?.[0])}
          </Typography>
        ),
      },
      {
        icon: 'timezone',
        label: 'Time Zone',
        value: timeZone,
        content: (
          <Typography variant="body4" fontWeight={700} color="grey.700">
            {timeZone}
          </Typography>
        ),
      },
      {
        icon: 'university',
        label: 'Education',
        value: latestEducationItem,
        content: (
          <Typography variant="body4" fontWeight={700} color="grey.700">
            {latestEducationItem?.school?.name}
          </Typography>
        ),
      },
      {
        icon: 'language',
        label: 'Language Skills',
        value: personLanguages,
        content: (
          <Typography variant="body4" fontWeight={700} color="grey.700">
            {abbreviationsLanguages}
          </Typography>
        ),
      },
    ];
    return {
      currentRolesInCompany,
      allCurrentRoles,
      allPresentRoles,
      items: items,
      moreItems: moreItems,
    };
  }, [
    allCurrentRoles,
    allPresentRoles,
    api?.data?.department?.departments,
    api?.data?.department?.functions,
    api?.data?.department?.seniority,
    api?.data?.location,
    api?.data?.profile?.headline,
    api?.data?.profile?.title,
    api?.data?.statistics?.network?.followers_count,
    abbreviationsLanguages,
    companyName,
    currentRoleDuration,
    currentRolesInCompany,
    fullWorkExpDuration,
    latestEducationItem,
    personLanguages,
    previousEmploymentType,
    roleName,
    timeZone,
    wasPreviouslySelfEmployedOrFreelancer,
  ]);
}
